import React, { useState, useRef } from "react";
import { Box, SpaceBetween, Table, Alert, Badge } from "@cloudscape-design/components";
import { BulkEntry } from "../types";

interface Props {
  onParsed: (entries: BulkEntry[]) => void;
}

export const CsvUpload: React.FC<Props> = ({ onParsed }) => {
  const [entries, setEntries] = useState<BulkEntry[]>([]);
  const [error, setError] = useState("");
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Normalise date to YYYY-MM-DD regardless of input format
  const normaliseDate = (raw: string): string => {
    const trimmed = raw.trim();
    // Already ISO: 2026-08-08
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    // UK/EU format: DD/MM/YYYY or DD-MM-YYYY
    const ukMatch = trimmed.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (ukMatch) {
      return `${ukMatch[3]}-${ukMatch[2].padStart(2, "0")}-${ukMatch[1].padStart(2, "0")}`;
    }
    // Fallback: return as-is and let the backend validate
    return trimmed;
  };

  const parseCSV = (text: string) => {
    setError("");
    const lines = text.split("\n").filter(l => l.trim());
    const results: BulkEntry[] = [];

    let startIdx = 0;
    if (lines.length > 0 && (lines[0].toLowerCase().includes("title") || lines[0].toLowerCase().includes("date"))) {
      startIdx = 1;
    }

    for (let i = startIdx; i < lines.length; i++) {
      // Parse CSV line handling quoted fields with commas
      const parts: string[] = [];
      let current = "";
      let inQuotes = false;
      const line = lines[i];
      for (let c = 0; c < line.length; c++) {
        const ch = line[c];
        if (ch === '"') {
          inQuotes = !inQuotes;
        } else if (ch === ',' && !inQuotes) {
          parts.push(current.trim().replace(/^"|"$/g, ""));
          current = "";
        } else {
          current += ch;
        }
      }
      parts.push(current.trim().replace(/^"|"$/g, ""));

      if (parts.length >= 3) {
        const shift = parts[2].toLowerCase();
        const date = normaliseDate(parts[1]);
        // 4th column (optional): trades separated by | or ;
        const tradesRaw = parts.length >= 4 ? parts[3] : "";
        const trades = tradesRaw
          ? tradesRaw.split(/[|;]/).map(t => t.trim()).filter(t => t.length > 0)
          : [];
        // 5th column (optional): description
        const description = parts.length >= 5 ? parts[4].trim() : "";

        results.push({
          title: parts[0],
          date,
          shift: (shift === "day" || shift === "night" || shift === "morning") ? shift : "day",
          trades,
          description: description || undefined,
        });
      }
    }

    if (results.length === 0) {
      setError("No valid entries found. CSV must have columns: title, date (YYYY-MM-DD), shift, trades (optional), description (optional)");
    }

    setEntries(results);
    onParsed(results);
  };

  const handleFile = (file: File) => {
    if (!file.name.endsWith(".csv")) {
      setError("Please upload a .csv file");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => parseCSV(e.target?.result as string);
    reader.readAsText(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  return (
    <SpaceBetween size="m">
      <Box variant="small" color="text-status-inactive">
        Upload a CSV with columns: <strong>title, date (YYYY-MM-DD), shift (day/night/morning), trades (optional, | separated), description (optional)</strong>
        <br />
        <a href="/example-import.csv" download style={{ color: "#0972D3", textDecoration: "none", fontWeight: 600 }}>
          ⬇ Download example CSV
        </a>
      </Box>

      {error && <Alert type="error">{error}</Alert>}

      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        style={{
          border: `2px dashed ${isDragOver ? "#FF9900" : "#d5d9d9"}`,
          borderRadius: 8,
          padding: 32,
          textAlign: "center",
          cursor: "pointer",
          background: isDragOver ? "#fff8e8" : "transparent",
          transition: "all 0.2s",
        }}
      >
        <Box variant="p" color="text-status-inactive">
          Drop CSV file here or click to browse
        </Box>
        <input ref={inputRef} type="file" accept=".csv" style={{ display: "none" }} onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])} />
      </div>

      {entries.length > 0 && (
        <Table
          items={entries}
          columnDefinitions={[
            { id: "title", header: "Title", cell: item => item.title },
            { id: "date", header: "Date", cell: item => item.date, width: 120 },
            { id: "shift", header: "Shift", cell: item => item.shift, width: 90 },
            {
              id: "trades",
              header: "Trades",
              cell: item => (item.trades && item.trades.length > 0)
                ? item.trades.map(t => <Badge key={t} color="blue">{t}</Badge>)
                : <Box color="text-status-inactive">—</Box>,
              width: 160,
            },
            {
              id: "description",
              header: "Description",
              cell: item => item.description || <Box color="text-status-inactive">—</Box>,
            },
          ]}
          header={<Box variant="strong">{entries.length} entries ready to import</Box>}
        />
      )}
    </SpaceBetween>
  );
};
